import { API_BACKEND_URL, API_DJANGO_URL } from "../config/config.mjs";
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Fix import for jwtDecode
import NodeCache from 'node-cache';

// Create an Axios instance for public API calls without authentication
const apiPublic = axios.create({
  baseURL: API_DJANGO_URL,
  headers: { "Content-Type": "application/json" },
  // ganz wichtig: KEIN Authorization-Header automatisch!
});


// Create an Axios instance for Django API, with "withCredentials"
const api_django = axios.create({
  baseURL: API_DJANGO_URL,
  withCredentials: true,
});

// Optional: Include CSRF token if needed
function getCSRFToken() {
  const csrfCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return csrfCookie;
}

api_django.interceptors.request.use(
  (config) => {
    // Include CSRF token in headers if present
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to check if the token is expired
function isTokenExpired(token) {
  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000;
    return decoded.exp < now;
  } catch (error) {
    console.error('Invalid token specified:', error);
    return true;
  }
}

let cachedAccessToken = null;
let cachedAccessTokenExpiry = null;

// Erstelle einen Cache für Benutzerinformationen und Benutzer-Apps mit einer Größe von 100 Einträgen und einer Gültigkeitsdauer von 5 Minuten
const userCache = new NodeCache({ stdTTL: 300, checkperiod: 60 });
const userAppsCache = new NodeCache({ stdTTL: 300, checkperiod: 60 });

async function refreshTokenIfNeeded() {
  const now = Date.now() / 1000;

  // Prüfe, ob der aktuelle Token bald abläuft oder bereits abgelaufen ist
  if (cachedAccessToken && cachedAccessTokenExpiry > now + 60) {
    return cachedAccessToken;  // Verwende den im Cache gespeicherten Access-Token
  }

  // Access Token oder Refresh Token aus localStorage laden
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  if (accessToken) {
    try {
      const decodedAccessToken = jwtDecode(accessToken);
      if (decodedAccessToken.exp > now + 60) {
        // Der Access-Token ist noch gültig und braucht nicht erneuert zu werden
        cachedAccessToken = accessToken;
        cachedAccessTokenExpiry = decodedAccessToken.exp;
        return accessToken;
      }
    } catch (error) {
      console.error('Invalid access token:', error);
    }
  }

  if (refreshToken) {
    try {
      // Token-Refresh durchführen, falls der Access-Token abgelaufen ist
      const refreshResponse = await api_django.post('/api-roby/token/refresh/', {
        refresh: refreshToken,
      });

      if (refreshResponse.status === 200) {
        const { access } = refreshResponse.data;
        localStorage.setItem('access_token', access);
        cachedAccessToken = access;  // Cache den neuen Token
        cachedAccessTokenExpiry = jwtDecode(access).exp;
        return access;
      }
    } catch (error) {
      console.error('Fehler beim Token-Refresh', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login/';
      return null;
    }
  } else {
    console.error('Kein Refresh Token gefunden.');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login/';
  }

  return null;
}

// Funktion, um Benutzerinformationen zu cachen
async function getCachedUserInfo() {
  const cachedUser = userCache.get('current_user');
  if (cachedUser) {
    return cachedUser;
  }

  try {
    const response = await api_django.get('/api-roby/user/info/');
    const userInfo = response.data;
    userCache.set('current_user', userInfo);
    return userInfo;
  } catch (error) {
    console.error('Fehler beim Abrufen der Benutzerinformationen', error);
    throw error;
  }
}

// Funktion, um Benutzer-Apps zu cachen
async function getCachedUserApps() {
  const cachedUserApps = userAppsCache.get('user_apps');
  if (cachedUserApps) {
    return cachedUserApps;
  }

  try {
    const response = await api_django.get('/api-roby/user/apps');
    const userApps = response.data;
    userAppsCache.set('user_apps', userApps);
    return userApps;
  } catch (error) {
    console.error('Fehler beim Abrufen der Benutzer-Apps', error);
    throw error;
  }
}

// Create an Axios instance for the backend without token in the header
const api = axios.create({
  baseURL: API_BACKEND_URL,
  withCredentials: true, // Allow credentials like cookies
  timeout: 300000, // 300 Sekunden Timeout (Backend kann langsam sein)
});

api.interceptors.request.use(
  async (config) => {
    // Prüfe, ob der Access Token im Cache vorhanden und noch gültig ist
    if (!cachedAccessToken || isTokenExpired(cachedAccessToken)) {
      // Wenn der Token nicht vorhanden oder abgelaufen ist, erneuere ihn
      cachedAccessToken = await refreshTokenIfNeeded();
    }

    // Füge den Bearer-Token in den Authorization-Header ein, wenn vorhanden
    if (cachedAccessToken) {
      config.headers.Authorization = `Bearer ${cachedAccessToken}`;  // Setze den Token in den Header
    }

    // Hole den CSRF-Token und füge ihn in den Header ein, wenn vorhanden
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// Interceptor to handle 401 Unauthorized responses
api.interceptors.response.use(
  (response) => {
    return response;  // Erfolgreiche Antwort einfach weiterleiten
  },
  async (error) => {
    const originalRequest = error.config;

    // Wenn der Access Token abgelaufen ist (401 Unauthorized)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;  // Verhindert Endlosschleifen

      const newAccessToken = await refreshTokenIfNeeded();
      if (newAccessToken) {
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } else {
        if (!originalRequest.url.endsWith('/api/login/')) {
          window.location.href = '/login';
        }  // Falls das Erneuern fehlschlägt, leite zum Login weiter
      }
    }

    return Promise.reject(error);  // Fehler weiterleiten, falls keine Lösung gefunden wird
  }
);

// Funktion, um die Token-Erneuerung periodisch zu planen
function scheduleTokenRefresh() {
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) {
    try {
      const decoded = jwtDecode(accessToken);
      const now = Date.now() / 1000;
      const expiresIn = decoded.exp - now - 60; // 1 Minute vor Ablaufzeitpunkt erneuern

      if (expiresIn > 0) {
        setTimeout(async () => {
          await refreshTokenIfNeeded();
          scheduleTokenRefresh();  // Plane die nächste Erneuerung
        }, expiresIn * 1000);
      }
    } catch (error) {
      console.error('Invalid access token:', error);
    }
  }
}

// Rufe diese Funktion beim Start der Anwendung auf, um die Erneuerung zu planen
scheduleTokenRefresh();

// Funktion zur Weiterleitung nach erfolgreichem Login
async function handleLoginRedirect() {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && accessToken.split('.').length === 3 && !isTokenExpired(accessToken)) {
      if (window.location.pathname === '/api-roby/login/') {
        window.location.href = '/roby/';
      }
    }
  } catch (error) {
    console.error('Error during login redirect:', error);
    window.location.href = '/login/';  // Fallback to login if something goes wrong
  }
}

// Rufe diese Funktion nach dem Laden der Seite auf, um nach erfolgreichem Login weiterzuleiten
handleLoginRedirect();

// Export both Axios instances
export { api, api_django, getCachedUserInfo, getCachedUserApps, apiPublic};